import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { WellnessSignal } from "@/components/wellness-signal";
import { usePagination } from "@/hooks/use-pagination";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type {
  ExtendedSubscriptionPlan,
  FailedPayment,
  Subscription,
  SubscriptionStats,
  TrialExpiringSoon,
} from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  ArrowUpRight,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  Gift,
  Heart,
  MoreHorizontal,
  Pause,
  Play,
  RefreshCw,
  Search,
  TrendingUp,
  UserCircle,
  Users,
  XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getPlanBadgeVariant(
  plan: ExtendedSubscriptionPlan,
): "default" | "secondary" | "outline" | "destructive" {
  switch (plan) {
    case "active":
    case "lifetime":
      return "default";
    case "trial":
      return "secondary";
    case "paused":
      return "outline";
    case "cancelled":
    case "expired":
      return "destructive";
    default:
      return "outline";
  }
}

export default function Subscriptions() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("members");
  const [selectedAction, setSelectedAction] = useState<{
    type: "extend" | "pause" | "resume" | "cancel" | "grant";
    appUserId: string;
    userName: string | null;
  } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [extendDays, setExtendDays] = useState(7);
  const [grantMonths, setGrantMonths] = useState(1);

  const canViewDashboard = Boolean(
    user && hasPermission(user.roles, "subscriptions", "view_dashboard"),
  );

  const { data: stats, isLoading: statsLoading } = useQuery<SubscriptionStats>({
    queryKey: ["/api/subscriptions/stats"],
    enabled: canViewDashboard,
  });

  const { data: subscriptions, isLoading: subscriptionsLoading } = useQuery<
    Subscription[]
  >({
    queryKey: ["/api/subscriptions"],
    enabled: canViewDashboard,
  });

  const { data: expiringTrials, isLoading: trialsLoading } = useQuery<
    TrialExpiringSoon[]
  >({
    queryKey: ["/api/subscriptions/trials-expiring"],
    enabled: canViewDashboard,
  });

  const { data: failedPayments, isLoading: failedPaymentsLoading } = useQuery<
    FailedPayment[]
  >({
    queryKey: ["/api/subscriptions/failed-payments"],
    enabled: canViewDashboard,
  });

  const unresolvedFailedPayments = useMemo(
    () => failedPayments?.filter((p) => p.status !== "resolved") ?? [],
    [failedPayments],
  );

  const describeError = (err: unknown): string => {
    if (err instanceof Error) {
      const match = err.message.match(/^\d{3}:\s*(.+)$/);
      if (match) {
        try {
          const parsed = JSON.parse(match[1]) as { message?: string | string[] };
          if (Array.isArray(parsed.message)) return parsed.message.join(", ");
          if (parsed.message) return parsed.message;
        } catch {
          return match[1];
        }
      }
      return err.message;
    }
    return "Unknown error";
  };

  const updateFailedPaymentMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/subscriptions/failed-payments/${id}`, {
        status,
      });
    },
    onSuccess: () => {
      toast({ title: "Payment status updated" });
      queryClient.invalidateQueries({
        queryKey: ["/api/subscriptions/failed-payments"],
      });
    },
    onError: (err) => {
      toast({
        title: "Failed to update payment status",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const extendTrialMutation = useMutation({
    mutationFn: async ({
      appUserId,
      days,
      reason,
    }: {
      appUserId: string;
      days: number;
      reason: string;
    }) => {
      return apiRequest(
        "POST",
        `/api/subscriptions/${appUserId}/extend-trial`,
        { days, reason },
      );
    },
    onSuccess: () => {
      toast({ title: "Trial extended successfully" });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/stats"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/subscriptions/trials-expiring"],
      });
      setSelectedAction(null);
      setActionReason("");
    },
    onError: (err) => {
      toast({
        title: "Failed to extend trial",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const pauseMutation = useMutation({
    mutationFn: async ({
      appUserId,
      reason,
    }: {
      appUserId: string;
      reason: string;
    }) => {
      return apiRequest("POST", `/api/subscriptions/${appUserId}/pause`, {
        reason,
      });
    },
    onSuccess: () => {
      toast({ title: "Subscription paused" });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/stats"] });
      setSelectedAction(null);
      setActionReason("");
    },
    onError: (err) => {
      toast({
        title: "Failed to pause subscription",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async ({
      appUserId,
      reason,
    }: {
      appUserId: string;
      reason: string;
    }) => {
      return apiRequest("POST", `/api/subscriptions/${appUserId}/resume`, {
        reason,
      });
    },
    onSuccess: () => {
      toast({ title: "Subscription resumed" });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/stats"] });
      setSelectedAction(null);
      setActionReason("");
    },
    onError: (err) => {
      toast({
        title: "Failed to resume subscription",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({
      appUserId,
      reason,
    }: {
      appUserId: string;
      reason: string;
    }) => {
      return apiRequest("POST", `/api/subscriptions/${appUserId}/cancel`, {
        reason,
        confirmCancel: true,
      });
    },
    onSuccess: () => {
      toast({ title: "Subscription cancelled" });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/stats"] });
      setSelectedAction(null);
      setActionReason("");
    },
    onError: (err) => {
      toast({
        title: "Failed to cancel subscription",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const grantMutation = useMutation({
    mutationFn: async ({
      appUserId,
      months,
      reason,
    }: {
      appUserId: string;
      months: number;
      reason: string;
    }) => {
      return apiRequest(
        "POST",
        `/api/subscriptions/${appUserId}/grant-complimentary`,
        { months, reason },
      );
    },
    onSuccess: () => {
      toast({ title: "Complimentary access granted" });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/stats"] });
      setSelectedAction(null);
      setActionReason("");
    },
    onError: (err) => {
      toast({
        title: "Failed to grant access",
        description: describeError(err),
        variant: "destructive",
      });
    },
  });

  const handleAction = () => {
    if (!selectedAction || !actionReason.trim()) return;

    switch (selectedAction.type) {
      case "extend":
        extendTrialMutation.mutate({
          appUserId: selectedAction.appUserId,
          days: extendDays,
          reason: actionReason,
        });
        break;
      case "pause":
        pauseMutation.mutate({
          appUserId: selectedAction.appUserId,
          reason: actionReason,
        });
        break;
      case "resume":
        resumeMutation.mutate({
          appUserId: selectedAction.appUserId,
          reason: actionReason,
        });
        break;
      case "cancel":
        cancelMutation.mutate({
          appUserId: selectedAction.appUserId,
          reason: actionReason,
        });
        break;
      case "grant":
        grantMutation.mutate({
          appUserId: selectedAction.appUserId,
          months: grantMonths,
          reason: actionReason,
        });
        break;
    }
  };

  const canExtendTrial =
    user && hasPermission(user.roles, "subscriptions", "extend_trial");
  const canPause = user && hasPermission(user.roles, "subscriptions", "pause");
  const canResume =
    user && hasPermission(user.roles, "subscriptions", "resume");
  const canCancel =
    user && hasPermission(user.roles, "subscriptions", "cancel");
  const canGrant =
    user && hasPermission(user.roles, "subscriptions", "grant_complimentary");
  // Same rules as the server (admin-subscriptions.service.ts): only a live
  // subscription can be paused; cancel also covers past-due (still billed) rows.
  const showPause = (sub: Subscription) =>
    canPause &&
    sub.status !== "paused" &&
    !["free", "expired", "cancelled"].includes(sub.plan);
  const showCancel = (sub: Subscription) =>
    canCancel && !["expired", "cancelled"].includes(sub.status);
  const hasActions = (sub: Subscription) =>
    (sub.plan === "trial" && canExtendTrial) ||
    showPause(sub) ||
    (sub.status === "paused" && canResume) ||
    canGrant ||
    showCancel(sub);

  const filteredSubscriptions = subscriptions?.filter((sub) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sub.userName?.toLowerCase().includes(query) ||
      sub.maskedEmail?.toLowerCase().includes(query) ||
      sub.plan.toLowerCase().includes(query)
    );
  });

  const { pageItems: pageSubscriptions, ...paginationProps } = usePagination(
    filteredSubscriptions || [],
  );

  const isActionPending =
    extendTrialMutation.isPending ||
    pauseMutation.isPending ||
    resumeMutation.isPending ||
    cancelMutation.isPending ||
    grantMutation.isPending;

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Subscriptions
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage subscription plans and member care
          </p>
        </div>

        {statsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <WellnessSignal
              variant="rose"
              icon={<Heart className="w-5 h-5" />}
              title="Active Members"
              value={stats.activeSubscriptions.toString()}
              subtitle="Nurturing their wellness"
            />
            <WellnessSignal
              variant="sage"
              icon={<TrendingUp className="w-5 h-5" />}
              title="Monthly Revenue"
              value={formatCurrency(stats.monthlyRevenue)}
              subtitle="Community investment"
            />
            <WellnessSignal
              variant="lavender"
              icon={<Users className="w-5 h-5" />}
              title="Active Trials"
              value={stats.activeTrials.toString()}
              subtitle="Exploring their journey"
            />
            <WellnessSignal
              variant="sand"
              icon={<Clock className="w-5 h-5" />}
              title="Trials Ending Soon"
              value={stats.trialsExpiringSoon.toString()}
              subtitle="May need support"
            />
          </div>
        ) : null}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="members" data-testid="tab-members">
              <Users className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger
              value="failed-payments"
              data-testid="tab-failed-payments"
              className="gap-2"
            >
              <CreditCard className="w-4 h-4" />
              Failed Payments
              {unresolvedFailedPayments.length > 0 && (
                <Badge
                  variant="destructive"
                  className="ml-1 h-5 min-w-5 px-1.5"
                >
                  {unresolvedFailedPayments.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-0">
            {(expiringTrials?.length ?? 0) > 0 && (
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-serif font-medium text-foreground">
                      Members Who May Need Support
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Trial periods ending within the next 7 days
                    </p>
                  </div>
                </div>

                {trialsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-20 rounded-xl" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expiringTrials?.map((trial) => (
                      <Card
                        key={trial.id}
                        className="border-amber-200/50 dark:border-amber-800/30 bg-amber-50/30 dark:bg-amber-950/10"
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0">
                                <UserCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {trial.userName || "Anonymous"}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {trial.maskedEmail || "No email"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-6 flex-wrap">
                              <div className="text-left w-[70px]">
                                <p className="text-sm text-muted-foreground">
                                  Days Left
                                </p>
                                <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                                  {trial.daysRemaining}
                                </p>
                              </div>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`button-trial-actions-${trial.appUserId}`}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {canExtendTrial && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        setSelectedAction({
                                          type: "extend",
                                          appUserId: trial.appUserId,
                                          userName: trial.userName,
                                        })
                                      }
                                      data-testid={`button-extend-trial-${trial.appUserId}`}
                                    >
                                      <Calendar className="w-4 h-4 mr-2" />
                                      Extend Trial
                                    </DropdownMenuItem>
                                  )}
                                  {canGrant && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        setSelectedAction({
                                          type: "grant",
                                          appUserId: trial.appUserId,
                                          userName: trial.userName,
                                        })
                                      }
                                      data-testid={`button-grant-${trial.appUserId}`}
                                    >
                                      <Gift className="w-4 h-4 mr-2" />
                                      Grant Complimentary
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </section>
            )}

            <section>
              <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                <div>
                  <h2 className="text-xl font-serif font-medium text-foreground">
                    All Members
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {filteredSubscriptions?.length ?? 0} members in your
                    community
                  </p>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search members..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                    data-testid="input-search-subscriptions"
                  />
                </div>
              </div>

              {subscriptionsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-20 rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {pageSubscriptions.map((sub) => (
                    <Card
                      key={sub.id}
                      className="hover-elevate transition-all duration-300"
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <UserCircle className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">
                                {sub.userName || "Anonymous"}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">
                                {sub.maskedEmail || "No email"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 flex-wrap">
                            <Badge
                              variant={getPlanBadgeVariant(sub.plan)}
                              className="capitalize"
                            >
                              {sub.plan}
                            </Badge>
                            <div className="text-left w-[100px]">
                              <p className="text-xs text-muted-foreground">
                                Last Active
                              </p>
                              <p className="text-sm text-foreground truncate">
                                {sub.lastActiveAt
                                  ? formatDistanceToNow(
                                      new Date(sub.lastActiveAt),
                                      { addSuffix: true },
                                    )
                                  : "Never"}
                              </p>
                            </div>
                            {hasActions(sub) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`button-sub-actions-${sub.appUserId}`}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {sub.plan === "trial" && canExtendTrial && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setSelectedAction({
                                        type: "extend",
                                        appUserId: sub.appUserId,
                                        userName: sub.userName,
                                      })
                                    }
                                    data-testid={`button-extend-${sub.appUserId}`}
                                  >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Extend Trial
                                  </DropdownMenuItem>
                                )}
                                {showPause(sub) && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setSelectedAction({
                                        type: "pause",
                                        appUserId: sub.appUserId,
                                        userName: sub.userName,
                                      })
                                    }
                                    data-testid={`button-pause-${sub.appUserId}`}
                                  >
                                    <Pause className="w-4 h-4 mr-2" />
                                    Pause Subscription
                                  </DropdownMenuItem>
                                )}
                                {sub.status === "paused" && canResume && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setSelectedAction({
                                        type: "resume",
                                        appUserId: sub.appUserId,
                                        userName: sub.userName,
                                      })
                                    }
                                    data-testid={`button-resume-${sub.appUserId}`}
                                  >
                                    <Play className="w-4 h-4 mr-2" />
                                    Resume Subscription
                                  </DropdownMenuItem>
                                )}
                                {canGrant && (
                                  <DropdownMenuItem
                                    data-testid={`button-grant-sub-${sub.appUserId}`}
                                    onClick={() =>
                                      setSelectedAction({
                                        type: "grant",
                                        appUserId: sub.appUserId,
                                        userName: sub.userName,
                                      })
                                    }
                                  >
                                    <Gift className="w-4 h-4 mr-2" />
                                    Grant Complimentary
                                  </DropdownMenuItem>
                                )}
                                {showCancel(sub) && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() =>
                                        setSelectedAction({
                                          type: "cancel",
                                          appUserId: sub.appUserId,
                                          userName: sub.userName,
                                        })
                                      }
                                      data-testid={`button-cancel-${sub.appUserId}`}
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Cancel Subscription
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {filteredSubscriptions?.length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">No members found</p>
                    </div>
                  )}
                  <PaginationControls
                    currentPage={paginationProps.currentPage}
                    totalPages={paginationProps.totalPages}
                    totalItems={paginationProps.totalItems}
                    startIndex={paginationProps.startIndex}
                    endIndex={paginationProps.endIndex}
                    canGoNext={paginationProps.canGoNext}
                    canGoPrev={paginationProps.canGoPrev}
                    onPageChange={paginationProps.setPage}
                    onNext={paginationProps.nextPage}
                    onPrev={paginationProps.prevPage}
                  />
                </div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="failed-payments" className="mt-0">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-red-100 dark:bg-red-900/30">
                  <CreditCard className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-serif font-medium text-foreground">
                    Failed Payments
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {unresolvedFailedPayments.length} payments need attention
                  </p>
                </div>
              </div>

              {failedPaymentsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 rounded-xl" />
                  ))}
                </div>
              ) : unresolvedFailedPayments.length > 0 ? (
                <div className="space-y-3">
                  {unresolvedFailedPayments.map((payment) => (
                      <Card
                        key={payment.id}
                        data-testid={`card-failed-payment-${payment.id}`}
                        className={`${
                          payment.status === "escalated"
                            ? "border-red-200/50 dark:border-red-800/30 bg-red-50/30 dark:bg-red-950/10"
                            : ""
                        }`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-4 min-w-0">
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                                  payment.status === "escalated"
                                    ? "bg-red-100 dark:bg-red-900/50"
                                    : "bg-amber-100 dark:bg-amber-900/50"
                                }`}
                              >
                                <CreditCard
                                  className={`w-5 h-5 ${
                                    payment.status === "escalated"
                                      ? "text-red-600 dark:text-red-400"
                                      : "text-amber-600 dark:text-amber-400"
                                  }`}
                                />
                              </div>
                              <div className="min-w-0">
                                <p
                                  className="font-medium text-foreground truncate"
                                  data-testid={`text-payment-name-${payment.id}`}
                                >
                                  {payment.userName || "Anonymous"}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {payment.maskedEmail || "No email"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-6 flex-wrap">
                              <div className="text-left w-[80px]">
                                <p className="text-xs text-muted-foreground">
                                  Amount
                                </p>
                                <p
                                  className="text-sm font-semibold text-foreground"
                                  data-testid={`text-payment-amount-${payment.id}`}
                                >
                                  {formatCurrency(payment.amount)}
                                </p>
                              </div>
                              <div className="text-left w-[140px]">
                                <p className="text-xs text-muted-foreground">
                                  Reason
                                </p>
                                <p
                                  className="text-sm text-foreground truncate"
                                  data-testid={`text-payment-reason-${payment.id}`}
                                >
                                  {payment.reasonDisplay}
                                </p>
                              </div>
                              <div className="text-left w-[80px]">
                                <p className="text-xs text-muted-foreground">
                                  Attempts
                                </p>
                                <p
                                  className="text-sm text-foreground"
                                  data-testid={`text-payment-attempts-${payment.id}`}
                                >
                                  {payment.attemptCount}
                                </p>
                              </div>
                              <Badge
                                variant={
                                  payment.status === "escalated"
                                    ? "destructive"
                                    : "secondary"
                                }
                                className="capitalize"
                                data-testid={`badge-payment-status-${payment.id}`}
                              >
                                {payment.status}
                              </Badge>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`button-payment-actions-${payment.id}`}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {payment.status !== "resolved" && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        updateFailedPaymentMutation.mutate({
                                          id: payment.id,
                                          status: "resolved",
                                        })
                                      }
                                      data-testid={`button-resolve-${payment.id}`}
                                    >
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Mark Resolved
                                    </DropdownMenuItem>
                                  )}
                                  {payment.status === "pending" && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        updateFailedPaymentMutation.mutate({
                                          id: payment.id,
                                          status: "escalated",
                                        })
                                      }
                                      data-testid={`button-escalate-${payment.id}`}
                                    >
                                      <ArrowUpRight className="w-4 h-4 mr-2" />
                                      Escalate
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          {payment.nextRetryAt && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <RefreshCw className="w-4 h-4" />
                                <span>
                                  Next retry:{" "}
                                  {formatDistanceToNow(
                                    new Date(payment.nextRetryAt),
                                    { addSuffix: true },
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500/50 mb-4" />
                  <p className="text-muted-foreground">No failed payments</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    All payments are up to date
                  </p>
                </div>
              )}
            </section>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog
        open={!!selectedAction}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAction(null);
            setActionReason("");
            setExtendDays(7);
            setGrantMonths(1);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {selectedAction?.type === "extend" && "Extend Trial Period"}
              {selectedAction?.type === "pause" && "Pause Subscription"}
              {selectedAction?.type === "resume" && "Resume Subscription"}
              {selectedAction?.type === "cancel" && "Cancel Subscription"}
              {selectedAction?.type === "grant" && "Grant Complimentary Access"}
            </DialogTitle>
            <DialogDescription>
              {selectedAction?.userName
                ? `For ${selectedAction.userName}`
                : "For this member"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedAction?.type === "extend" && (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Extend by (days)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={30}
                  value={extendDays}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setExtendDays(Number.isFinite(n) ? n : 7);
                  }}
                  data-testid="input-extend-days"
                />
              </div>
            )}

            {selectedAction?.type === "grant" && (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Grant for (months)
                </label>
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={grantMonths}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setGrantMonths(Number.isFinite(n) ? n : 1);
                  }}
                  data-testid="input-grant-months"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Reason (required)
              </label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Please provide a reason for this action..."
                className="min-h-[100px]"
                data-testid="textarea-action-reason"
              />
            </div>

            {selectedAction?.type === "cancel" && (
              <div className="p-4 bg-destructive/10 rounded-xl border border-destructive/20">
                <p className="text-sm text-destructive font-medium">
                  This action will cancel the member's subscription. They will
                  lose access at the end of their current billing period.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedAction(null);
                setActionReason("");
                setExtendDays(7);
                setGrantMonths(1);
              }}
              disabled={isActionPending}
              data-testid="button-cancel-action"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={!actionReason.trim() || isActionPending}
              variant={
                selectedAction?.type === "cancel" ? "destructive" : "default"
              }
              data-testid="button-confirm-action"
            >
              {isActionPending ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
